/**
 * Aviso «vas a subir muchos datos con tu plan móvil» (round 2026-10-27).
 *
 * Las aseadoras suben videos de cientos de MB desde el celular, con su propio plan
 * de datos. Antes de encolar archivos que sumen más de 50 MB (`UMBRAL_AVISO_DATOS_BYTES`)
 * y solo si el navegador dice que la conexión es móvil (`utils/conexion.ts`), se
 * pide confirmación. Si la API de conexión no existe (iPhone / Safari, escritorio)
 * no se pregunta: no se puede saber y no se inventa.
 *
 * Es un `<Modal>` controlado por un hook, no `Modal.confirm`: así vive dentro del
 * árbol de React (contexto de AntD, tests) y se cierra solo si el panel se desmonta.
 *
 * Reglas:
 *  - «Cancelar» (botón, X o Esc) no encola nada.
 *  - «No volver a preguntar en este dispositivo» solo se guarda si la persona
 *    acepta («Subir ahora»); marcarla y cancelar no deja la preferencia grabada.
 *  - Varias consultas seguidas (arrastrar varios archivos en escritorio entrega uno
 *    por llamada) se atienden de a una, y una respuesta «no volver a preguntar»
 *    vale para las que siguen.
 *  - No bloquea la escritura de texto: el modal solo aparece al agregar archivos.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Checkbox, Modal } from "antd";
import { formatBytes } from "../../../utils/colaSubida";
import {
  debeAvisarDatosMoviles,
  guardarNoPreguntarDatos,
  leerNoPreguntarDatos,
  motivoDatosMoviles,
  type MotivoDatosMoviles,
} from "../../../utils/conexion";

interface Consulta {
  bytes: number;
  cantidad: number;
  motivo: MotivoDatosMoviles;
  resolver: (acepta: boolean) => void;
}

const TEXTO_MOTIVO: Record<MotivoDatosMoviles, string> = {
  celular: "Tu celular está usando datos móviles.",
  ahorro_de_datos: "Tu celular tiene activado el ahorro de datos.",
  conexion_lenta: "Tu conexión a internet es lenta (parece de datos móviles).",
};

export function useAvisoDatosMoviles() {
  const [consulta, setConsulta] = useState<Consulta | null>(null);
  const [noVolver, setNoVolver] = useState(false);
  const cadena = useRef<Promise<unknown>>(Promise.resolve());
  const montado = useRef(true);
  const resolverActual = useRef<((acepta: boolean) => void) | null>(null);

  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
      // Panel cerrado con el aviso abierto: cuenta como «Cancelar».
      resolverActual.current?.(false);
      resolverActual.current = null;
    };
  }, []);

  const preguntar = useCallback((bytes: number, cantidad: number): Promise<boolean> => {
    if (!debeAvisarDatosMoviles(bytes)) return Promise.resolve(true);
    if (leerNoPreguntarDatos()) return Promise.resolve(true);
    const motivo = motivoDatosMoviles();
    if (!motivo) return Promise.resolve(true);
    if (!montado.current) return Promise.resolve(false);
    return new Promise<boolean>((resolve) => {
      const resolver = (acepta: boolean) => {
        resolverActual.current = null;
        resolve(acepta);
      };
      resolverActual.current = resolver;
      setNoVolver(false);
      setConsulta({ bytes, cantidad, motivo, resolver });
    });
  }, []);

  /** `true` si se puede encolar (no hace falta avisar, o la persona aceptó); `false` si canceló. */
  const confirmar = useCallback(
    (files: readonly File[]): Promise<boolean> => {
      const bytes = files.reduce((suma, f) => suma + (f.size || 0), 0);
      const turno = cadena.current.then(() => preguntar(bytes, files.length));
      cadena.current = turno.catch(() => undefined);
      return turno;
    },
    [preguntar],
  );

  const cerrar = (acepta: boolean) => {
    if (!consulta) return;
    if (acepta && noVolver) guardarNoPreguntarDatos(true);
    const { resolver } = consulta;
    setConsulta(null);
    setNoVolver(false);
    resolver(acepta);
  };

  const modal = (
    <Modal
      open={consulta !== null}
      centered
      maskClosable={false}
      destroyOnClose
      title="Vas a subir archivos pesados"
      onCancel={() => cerrar(false)}
      footer={
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <Button size="large" data-testid="nm-datos-cancelar" onClick={() => cerrar(false)}>
            Cancelar
          </Button>
          <Button
            type="primary"
            size="large"
            data-testid="nm-datos-subir"
            onClick={() => cerrar(true)}
          >
            Subir ahora
          </Button>
        </div>
      }
    >
      {consulta && (
        <div data-testid="nm-datos-texto">
          <p style={{ marginBottom: 8 }}>
            {TEXTO_MOTIVO[consulta.motivo]} Vas a subir{" "}
            <strong>
              {consulta.cantidad === 1 ? "1 archivo" : `${consulta.cantidad} archivos`} (
              {formatBytes(consulta.bytes)})
            </strong>
            , lo que puede gastar buena parte de tu plan de datos.
          </p>
          <p style={{ marginBottom: 12 }}>
            Si puedes, conéctate a una red wifi antes de subirlos. ¿Quieres subirlos ahora?
          </p>
          <Checkbox
            checked={noVolver}
            onChange={(e) => setNoVolver(e.target.checked)}
            data-testid="nm-datos-no-volver"
          >
            No volver a preguntar en este dispositivo
          </Checkbox>
        </div>
      )}
    </Modal>
  );

  return { confirmar, modal };
}
